/**
 * คำนวณราคา CF session — ส่วนลดต่อชิ้นตาม S3 (list vs product override)
 * Pure functions — ใช้ตอน match/bridge และ unit test
 */

export type ProductDiscountRules = {
  discountPercent: number | null;
  discountFixedLak: number | null;
};

export type ListDiscountRules = {
  listDiscountPercent: number | null;
  listDiscountFixedLak: number | null;
};

export type SessionPromoRules = ListDiscountRules & {
  freeShipping: boolean;
  shippingFeeLak: number | null;
};

export type SessionOptionLine = {
  adminOptionDefinitionId: string;
  labelSnapshot: string;
  surchargeLak: number;
  requiresCustomerInput: boolean;
};

export type PricingSnapshotJson = {
  version: 1 | 2;
  productUnitPriceLak: number;
  unitAfterDiscountLak: number;
  optionsPerUnitLak: number;
  discountSource: "list" | "product" | "none";
  productDiscount: ProductDiscountRules;
  listDiscount: ListDiscountRules;
  options: Array<{
    adminOptionDefinitionId: string;
    labelSnapshot: string;
    surchargeLak: number;
    requiresCustomerInput: boolean;
  }>;
  promo: {
    freeShipping: boolean;
    shippingFeeLak: number | null;
  };
  computedAt: string;
};

/** ยอดส่วนลด LAK ต้องเป็นหลักพัน (x,000) */
export const DISCOUNT_LAK_STEP = 1000;

/**
 * ปัดยอดส่วนลด LAK เป็นหลักพันที่ใกล้ที่สุด (เช่น 12,450 → 12,000; 12,500 → 13,000)
 */
export function roundDiscountLakToThousand(amount: number): number {
  if (!Number.isFinite(amount) || amount <= 0) return 0;
  return Math.round(amount / DISCOUNT_LAK_STEP) * DISCOUNT_LAK_STEP;
}

/** normalize ค่า fixed LAK ก่อนบันทึก — 0 หลังปัด = ไม่มีส่วนลดคงที่ */
export function normalizeDiscountFixedLak(
  amount: number | null | undefined,
): number | null {
  if (amount == null) return null;
  if (!Number.isFinite(amount) || amount < 0) return null;
  const rounded = roundDiscountLakToThousand(amount);
  return rounded > 0 ? rounded : null;
}

export function parseDiscountPercent(
  raw: string | number | null | undefined,
): number | null {
  if (raw == null || raw === "") return null;
  const n = typeof raw === "number" ? raw : Number(raw);
  if (!Number.isFinite(n)) return null;
  return n;
}

export function resolveShippingFeeLak(rules: {
  freeShipping: boolean;
  shippingFeeLak: number | null;
}): number {
  if (rules.freeShipping) return 0;
  if (rules.shippingFeeLak == null) return 0;
  return Math.max(0, Math.round(rules.shippingFeeLak));
}

/** รวม surcharge option ต่อหน่วย */
export function sumOptionsPerUnitLak(options: SessionOptionLine[]): number {
  let sum = 0;
  for (const o of options) {
    sum += Math.max(0, Math.round(o.surchargeLak));
  }
  return sum;
}

export function merchandiseLineLak(input: {
  productUnitPriceLak: number;
  optionsPerUnitLak: number;
  quantity: number;
}): number {
  const unit =
    Math.round(input.productUnitPriceLak) + Math.round(input.optionsPerUnitLak);
  return unit * Math.max(0, Math.round(input.quantity));
}

/** มีส่วนลดรายตัวเมื่อมีฟิลด์ non-null อย่างน้อยหนึ่ง (S3 override) */
export function hasProductDiscountOverride(
  product: ProductDiscountRules,
): boolean {
  return product.discountPercent != null || product.discountFixedLak != null;
}

/**
 * ลดราคาตัวสินค้าต่อชิ้น: % ก่อน แล้วหัก LAK — ไม่ต่ำกว่า 0
 * ยอดส่วนลด (ทั้งจาก % และ fixed) ปัดเป็นหลักพัน (x,000)
 */
export function applyUnitDiscountLak(input: {
  productUnitPriceLak: number;
  discountPercent: number | null;
  discountFixedLak: number | null;
}): number {
  const base = Math.max(0, Math.round(input.productUnitPriceLak));
  let after = base;
  const percent = input.discountPercent;
  if (percent != null && percent > 0) {
    const pct = Math.min(100, percent);
    const rawAfter = Math.round((base * (100 - pct)) / 100);
    const percentOff = roundDiscountLakToThousand(base - rawAfter);
    after = Math.max(0, base - percentOff);
  }
  const fixed =
    input.discountFixedLak == null
      ? 0
      : roundDiscountLakToThousand(Math.max(0, input.discountFixedLak));
  return Math.max(0, after - fixed);
}

/** S3: รายตัว override list; ไม่มีรายตัว → ใช้ list */
export function resolveEffectiveUnitDiscount(input: {
  productUnitPriceLak: number;
  product: ProductDiscountRules;
  list: ListDiscountRules;
}): {
  unitAfterDiscountLak: number;
  discountSource: "list" | "product" | "none";
  applied: ProductDiscountRules;
  discountLak: number;
} {
  const base = Math.max(0, Math.round(input.productUnitPriceLak));
  if (hasProductDiscountOverride(input.product)) {
    const unitAfterDiscountLak = applyUnitDiscountLak({
      productUnitPriceLak: base,
      discountPercent: input.product.discountPercent,
      discountFixedLak: input.product.discountFixedLak,
    });
    return {
      unitAfterDiscountLak,
      discountSource: "product",
      applied: {
        discountPercent: input.product.discountPercent,
        discountFixedLak: input.product.discountFixedLak,
      },
      discountLak: Math.max(0, base - unitAfterDiscountLak),
    };
  }

  const listHas =
    input.list.listDiscountPercent != null ||
    input.list.listDiscountFixedLak != null;
  if (!listHas) {
    return {
      unitAfterDiscountLak: base,
      discountSource: "none",
      applied: { discountPercent: null, discountFixedLak: null },
      discountLak: 0,
    };
  }

  const unitAfterDiscountLak = applyUnitDiscountLak({
    productUnitPriceLak: base,
    discountPercent: input.list.listDiscountPercent,
    discountFixedLak: input.list.listDiscountFixedLak,
  });
  return {
    unitAfterDiscountLak,
    discountSource: "list",
    applied: {
      discountPercent: input.list.listDiscountPercent,
      discountFixedLak: input.list.listDiscountFixedLak,
    },
    discountLak: Math.max(0, base - unitAfterDiscountLak),
  };
}

export function buildPricingSnapshot(input: {
  productUnitPriceLak: number;
  unitAfterDiscountLak: number;
  discountSource: "list" | "product" | "none";
  product: ProductDiscountRules;
  list: ListDiscountRules;
  options: SessionOptionLine[];
  promo: { freeShipping: boolean; shippingFeeLak: number | null };
  computedAt: string;
}): PricingSnapshotJson {
  const optionsPerUnitLak = sumOptionsPerUnitLak(input.options);
  return {
    version: 2,
    productUnitPriceLak: Math.round(input.productUnitPriceLak),
    unitAfterDiscountLak: Math.round(input.unitAfterDiscountLak),
    optionsPerUnitLak,
    discountSource: input.discountSource,
    productDiscount: input.product,
    listDiscount: input.list,
    options: input.options.map((o) => ({
      adminOptionDefinitionId: o.adminOptionDefinitionId,
      labelSnapshot: o.labelSnapshot,
      surchargeLak: Math.round(o.surchargeLak),
      requiresCustomerInput: o.requiresCustomerInput,
    })),
    promo: {
      freeShipping: input.promo.freeShipping,
      shippingFeeLak: input.promo.shippingFeeLak,
    },
    computedAt: input.computedAt,
  };
}

/** คำนวณบรรทัดเดียวตามสูตร S3 */
export function priceSingleLineOrder(input: {
  productUnitPriceLak: number;
  options: SessionOptionLine[];
  quantity: number;
  productDiscount: ProductDiscountRules;
  listDiscount: ListDiscountRules;
  promo: { freeShipping: boolean; shippingFeeLak: number | null };
  computedAt: string;
}): {
  unitPriceLak: number;
  unitAfterDiscountLak: number;
  optionsSurchargeLak: number;
  sessionDiscountLak: number;
  totalPriceLak: number;
  shippingFeeLak: number;
  discountSource: "list" | "product" | "none";
  snapshot: PricingSnapshotJson;
} {
  const quantity = Math.max(0, Math.round(input.quantity));
  const resolved = resolveEffectiveUnitDiscount({
    productUnitPriceLak: input.productUnitPriceLak,
    product: input.productDiscount,
    list: input.listDiscount,
  });
  const optionsPerUnitLak = sumOptionsPerUnitLak(input.options);
  const optionsSurchargeLak = optionsPerUnitLak * quantity;
  const unitCharged = resolved.unitAfterDiscountLak + optionsPerUnitLak;
  const totalPriceLak = unitCharged * quantity;
  const sessionDiscountLak = resolved.discountLak * quantity;

  return {
    unitPriceLak: Math.round(input.productUnitPriceLak),
    unitAfterDiscountLak: resolved.unitAfterDiscountLak,
    optionsSurchargeLak,
    sessionDiscountLak,
    totalPriceLak,
    shippingFeeLak: resolveShippingFeeLak(input.promo),
    discountSource: resolved.discountSource,
    snapshot: buildPricingSnapshot({
      productUnitPriceLak: input.productUnitPriceLak,
      unitAfterDiscountLak: resolved.unitAfterDiscountLak,
      discountSource: resolved.discountSource,
      product: input.productDiscount,
      list: input.listDiscount,
      options: input.options,
      promo: input.promo,
      computedAt: input.computedAt,
    }),
  };
}

/** @deprecated เก็บไว้เพื่อ test เก่า — อย่าใช้ในโค้ดใหม่ */
export function computeSessionDiscountLak(_input: {
  merchandiseOrderLak: number;
  discountPercent: number | null;
  minTotalPriceLak: number | null;
  maxDiscountLak: number | null;
}): number {
  return 0;
}

/** @deprecated */
export function allocateSessionDiscountToLines(
  lineMerchandiseLak: number[],
  _sessionDiscountLak: number,
): number[] {
  return lineMerchandiseLak.map(() => 0);
}
